import { ChatMessageData, CyclingQRCodeData, QRCodeData } from "@kalyzee/kast-web-sdk/web-components";
import { ChatroomStatus, Meeting, MeetingRoom, MeetingSession } from "@kalyzee/kast-web-sdk/websocket";
import { PageMap } from "../../components/navigation/PageContext";
import { SessionMode } from "../../store/session/slices";
import {
  CarouselData,
  MessagingBridgeBroadcastChannelMessage,
  MessagingBridgeClientData,
  MessagingBridgeManager,
  MessagingBridgeManagerOptions,
  MessagingBridgeMessage,
  MessagingBridgeMessageAction,
  MessagingBridgeMessageExtended,
} from "./common";

const MASTER_PING_INTERVAL = 2000;

export interface MessagingBridgeManagerMasterOptions extends MessagingBridgeManagerOptions {}

export interface MessagingBridgeManagerMasterEventMap {
  clientJoin: (client: MessagingBridgeManagerMasterClient<any, any>) => void;
  clientLeave: (client: MessagingBridgeManagerMasterClient<any, any>) => void;
  clientChange: (client: MessagingBridgeManagerMasterClient<any, any>) => void;
  clientData: (client: MessagingBridgeManagerMasterClient<any, any>) => void;
  requestMeeting: (
    msg: MessagingBridgeMessageExtended<MessagingBridgeMessageAction.GET_MEETING>,
    respond: (meeting?: Meeting, rooms?: MeetingRoom[], session?: MeetingSession<false>) => void
  ) => void;
  requestChat: (
    msg: MessagingBridgeMessageExtended<MessagingBridgeMessageAction.GET_CHAT>,
    respond: (chatroomId: string, state: ChatroomStatus, messages: ChatMessageData[]) => void
  ) => void;
}

export interface MessagingBridgeManagerMasterClient<M extends SessionMode, T extends PageMap[M]> {
  id: string;
  currPingCounter: number;
  active: boolean;
  authorized: boolean;
  data?: MessagingBridgeClientData<M, T>;
}

export class MessagingBridgeManagerMaster extends MessagingBridgeManager<
  SessionMode.MASTER,
  keyof MessagingBridgeManagerMasterEventMap,
  MessagingBridgeManagerMasterEventMap
> {
  static pingCounter: number = 0;

  protected clients: Map<string, MessagingBridgeManagerMasterClient<any, any>> = new Map();

  private pingInterval?: NodeJS.Timer;

  constructor(options?: MessagingBridgeManagerOptions, clients?: MessagingBridgeManagerMasterClient<any, any>[]) {
    super(SessionMode.MASTER, options);

    this.appListener = [
      [
        "ready",
        () => {
          // Give the id to all slave (if master disconnect and reconnect)
          this.post(this.buildBridgeMessage(MessagingBridgeMessageAction.WELCOME, { masterId: this.id }));
        },
      ],
      [
        "applicationJoin",
        (id: string, password?: string) => {
          this.log("APPLICATION JOIN : ", id, password);
          const noPassword = password?.trim() ? false : true;
          const authorized = !this.password || (!noPassword && password?.trim() === this.password?.trim());
          this.addClient({ id, authorized: authorized, currPingCounter: MessagingBridgeManagerMaster.pingCounter, active: true });
          if (authorized) {
            this.post(this.buildBridgeMessage(MessagingBridgeMessageAction.AUTHORIZED, undefined), id);
            this.post(this.buildBridgeMessage(MessagingBridgeMessageAction.WELCOME, { masterId: this.id }), id);
          } else {
            this.post(this.buildBridgeMessage(MessagingBridgeMessageAction.UNAUTHORIZED, { code: noPassword ? -1 : -2 }), id);
          }
        },
      ],
      [
        "applicationLeave",
        (id: string) => {
          this.removeClient(id);
        },
      ],
      [
        "applicationMessage",
        (id: string, msg: MessagingBridgeMessageExtended<any>) => {
          // if (id !== msg.fromId) return;
          this.proccess(msg);
        },
      ],
    ];
    this.appListener.forEach((listener) => this.appMessage?.addEventListener(...listener));

    this.appBroadcastChannel?.addEventListener("applicationJoin", (id: string) => {
      // always authorized because it's a broadcast channel
      this.addClient({ id, authorized: true, currPingCounter: MessagingBridgeManagerMaster.pingCounter, active: true });
      this.post(this.buildBridgeMessage(MessagingBridgeMessageAction.AUTHORIZED, undefined), id);
      this.post(this.buildBridgeMessage(MessagingBridgeMessageAction.WELCOME, { masterId: this.id }), id);
    });
    this.appBroadcastChannel?.addEventListener("applicationLeave", (id: string) => {
      this.removeClient(id);
    });
    this.appBroadcastChannel?.addEventListener("applicationMessage", (id: string, msg: MessagingBridgeBroadcastChannelMessage<any>) => {
      if (id !== msg.fromId) return;
      this.proccess(msg, msg.toId);
    });

    // Init Clients
    if (clients) {
      clients.forEach((client) => {
        const newClient = { ...client };
        newClient.currPingCounter = MessagingBridgeManagerMaster.pingCounter;
        this.addClient(newClient);
      });
    }

    // Give the id to all slave (if master disconnect and reconnect)
    this.post(this.buildBridgeMessage(MessagingBridgeMessageAction.WELCOME, { masterId: this.id }));

    if (MASTER_PING_INTERVAL > 0) {
      this.pingInterval = setInterval(() => {
        this.clients.forEach((data) => {
          if (MessagingBridgeManagerMaster.pingCounter >= data.currPingCounter + 5) {
            const lastActiveValue = data.active;
            data.active = false;
            if (lastActiveValue) {
              this.events.triggerEvent("clientChange", data);
            }
          }
        });
        this.postPing();
      }, MASTER_PING_INTERVAL);
    }
  }

  post<T extends MessagingBridgeMessageAction>(
    msg: MessagingBridgeMessage<T>, 
    toId?: string,
    priority?: boolean,
  ): MessagingBridgeMessage<T> | undefined {
    this.log("POST message : ", msg, " toId : ", toId);
    const extendedMsg: MessagingBridgeMessageExtended<T> = {
      ...msg,
      fromId: this.id,
      from: this.mode,
    };
    if (this.appBroadcastChannel) {
      const broadcastMessage: MessagingBridgeBroadcastChannelMessage<T> = {
        ...extendedMsg,
        toId,
      };
      this.appBroadcastChannel.postApplicationMessage(this.id, broadcastMessage, priority);
    }
    this.appMessage?.postApplicationMessage(extendedMsg, toId, priority);
    return msg;
  }

  protected proccess(msg: MessagingBridgeMessageExtended<any>, toId?: string): void {
    this.log("RECEIVE message : ", msg, " toId : ", toId, this.id);
    if (toId && toId !== this.id) return;
    const clientId = msg.fromId;
    if (!clientId) return;
    const client = this.clients.get(clientId);
    this.log("RECEIVE message CLIENT : ", clientId, client, this.clients);
    if (!client) {
      this.appBroadcastChannel?.requestJoinApplication(clientId);
      return;
    } else if (!client.authorized) {
      return;
    }
    if (MessagingBridgeManagerMaster.isMessageExtented(msg, MessagingBridgeMessageAction.PONG)) {
      const lastActiveValue = client.active;
      client.active = true;
      client.currPingCounter = msg.content.counter;
      if (!lastActiveValue) {
        this.events.triggerEvent("clientChange", client);
      }
      return;
    }
    if (MessagingBridgeManagerMaster.isMessageExtented(msg, MessagingBridgeMessageAction.SET_CLIENT_DATA)) {
      const data: MessagingBridgeClientData<any, any> = {
        mode: msg.content.mode,
        page: msg.content.page,
        data: msg.content.data,
      };
      client.data = data;
      this.events.triggerEvent("clientData", client);
      this.events.triggerEvent("clientChange", client);
      return;
    }
    if (MessagingBridgeManagerMaster.isMessageExtented(msg, MessagingBridgeMessageAction.CLEAR_CLIENT_DATA)) {
      if (client.data?.page !== msg.content.page) return;
      client.data = undefined;
      this.events.triggerEvent("clientData", client);
      this.events.triggerEvent("clientChange", client);

      return;
    }
    if (MessagingBridgeManagerMaster.isMessageExtented(msg, MessagingBridgeMessageAction.GET_MEETING)) {
      this.events.triggerEvent("requestMeeting", msg, (meeting?: Meeting, rooms?: MeetingRoom[], session?: MeetingSession<false>) => {
        this.postMeeting(meeting, rooms, session, msg.fromId);
      });
      return;
    }
    if (MessagingBridgeManagerMaster.isMessageExtented(msg, MessagingBridgeMessageAction.GET_CHAT)) {
      this.events.triggerEvent("requestChat", msg, (chatroomId: string, state: ChatroomStatus, messages: ChatMessageData[]) => {
        this.postChat(chatroomId, state, messages, msg.fromId);
      });
      return;
    }
  }

  public destroy() {
    if (this.pingInterval) clearInterval(this.pingInterval);
    super.destroy();
  }

  // --------------------------- CLIENT --------------------- //

  private addClient<M extends SessionMode, T extends PageMap[M]>(data: MessagingBridgeManagerMasterClient<M, T>) {
    this.clients.set(data.id, data);
    this.events.triggerEvent("clientJoin", data);
    this.events.triggerEvent("clientChange", data);
  }

  private removeClient(clientId: string) {
    const data = this.clients.get(clientId);
    this.clients.delete(clientId);
    if (!data) return;
    this.events.triggerEvent("clientChange", data);
    this.events.triggerEvent("clientLeave", data);
  }

  public getClients(onlyAuthorized: boolean = true): MessagingBridgeManagerMasterClient<any, any>[] {
    const clients = Array.from(this.clients.values());
    if (!onlyAuthorized) return clients.filter((c) => c.authorized);
    return clients;
  }

  public getActiveClients<M extends SessionMode = any, T extends PageMap[M] = any>(onlyAuthorized: boolean = true): MessagingBridgeManagerMasterClient<M, T>[] {
    return this.getClients(onlyAuthorized).filter((c) => c.active);
  }

  // --------------------------- POST --------------------- //

  private postPing() {
    MessagingBridgeManagerMaster.pingCounter += 1;
    const msg = this.buildBridgeMessage(MessagingBridgeMessageAction.PING, {
      counter: MessagingBridgeManagerMaster.pingCounter,
    });
    return this.post(msg);
  }

  postMeeting(meeting?: Meeting, rooms?: MeetingRoom[], session?: MeetingSession<false>, toId?: string) {
    const msg = this.buildBridgeMessage(MessagingBridgeMessageAction.SET_MEETING, {
      meeting,
      rooms,
      session,
    });
    return this.post(msg, toId);
  }

  postChat(chatroomId: string, state: ChatroomStatus, messages: ChatMessageData[], toId?: string) {
    const msg = this.buildBridgeMessage(MessagingBridgeMessageAction.SET_CHAT, {
      chatroomId,
      state,
      messages,
    });
    return this.post(msg, toId);
  }

  postDisplayCarousel(
    data: CarouselData, 
    delay: number, 
    page: number, 
    pageCount: number, 
    toId?: string
  ) {
    const msg = this.buildBridgeMessage(MessagingBridgeMessageAction.DISPLAY_CAROUSEL, {
      data,
      delay,
      page,
      pageCount
    });
    return this.post(msg, toId);
  }

  postQRCodeData(data: CyclingQRCodeData, toId?: string) {
    const msg = this.buildBridgeMessage(MessagingBridgeMessageAction.SET_QR_CODE_DATA, {
      data,
    });
    return this.post(msg, toId, true);
  }
}
